import { NurseLayout } from "@/components/layout/nurse-layout";
import { Shield, AlertTriangle, BookOpen, Clock, MessageSquare, CheckCircle2, XCircle, FileText } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function NurseAiGovernance() {
  return (
    <NurseLayout title="AI Governance">
      <div className="max-w-4xl mx-auto space-y-8">

        <div className="flex items-start gap-4">
          <div className="bg-teal-50 p-3 rounded-2xl">
            <Shield className="h-8 w-8 text-teal-600" />
          </div>
          <div>
            <h2 className="text-2xl font-bold text-slate-900">AI Governance Framework</h2>
            <p className="text-slate-500 mt-1">RheumHub Clinical AI Assistant — Policy document for rheumatology nurses</p>
            <div className="flex items-center gap-2 mt-2">
              <Badge className="bg-teal-100 text-teal-800 border-teal-200">System prompt v2025-05</Badge>
              <Badge className="bg-slate-100 text-slate-700 border-slate-200">Next review: November 2025</Badge>
            </div>
          </div>
        </div>

        <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <FileText className="h-5 w-5 text-teal-600" />
            1. AI Model in Use
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-slate-500 text-xs font-medium uppercase tracking-wide mb-1">Model</p>
              <p className="text-slate-900 font-semibold">GPT-5.4 (OpenAI)</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-slate-500 text-xs font-medium uppercase tracking-wide mb-1">Access</p>
              <p className="text-slate-900 font-semibold">Via Replit AI Integrations proxy</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-slate-500 text-xs font-medium uppercase tracking-wide mb-1">Max tokens per response</p>
              <p className="text-slate-900 font-semibold">8,192 tokens</p>
            </div>
            <div className="bg-slate-50 rounded-xl p-4">
              <p className="text-slate-500 text-xs font-medium uppercase tracking-wide mb-1">Rate limit</p>
              <p className="text-slate-900 font-semibold">30 messages per user per hour</p>
            </div>
          </div>
          <p className="text-sm text-slate-600">
            Message content is sent to OpenAI's API for inference only. Under our usage agreement, messages are not used to train OpenAI models.
          </p>
        </section>

        <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-teal-600" />
            2. Clinical Constraints and Grounding
          </h3>
          <p className="text-slate-700 leading-relaxed">
            The AI system prompt (version v2025-05) grounds the assistant in the following sources. All responses must reference these guidelines where relevant:
          </p>
          <ul className="space-y-2 text-slate-700 text-sm">
            {[
              { name: "NMC Code", desc: "The Code: Professional standards of practice and behaviour for nurses, midwives and nursing associates" },
              { name: "NICE guidelines", desc: "National Institute for Health and Care Excellence — rheumatology and related guidance" },
              { name: "BSR guidelines", desc: "British Society for Rheumatology" },
              { name: "EULAR guidelines", desc: "European Alliance of Associations for Rheumatology (where applicable to UK practice)" },
              { name: "ACR guidelines", desc: "American College of Rheumatology (where applicable to UK practice)" },
              { name: "RCN guidance", desc: "Royal College of Nursing" },
              { name: "BNF", desc: "British National Formulary — for medicines information" },
            ].map((g) => (
              <li key={g.name} className="flex items-start gap-3 bg-slate-50 rounded-xl p-3">
                <CheckCircle2 className="h-4 w-4 text-teal-500 shrink-0 mt-0.5" />
                <span><strong>{g.name}:</strong> {g.desc}</span>
              </li>
            ))}
          </ul>
        </section>

        <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <CheckCircle2 className="h-5 w-5 text-teal-600" />
            3. What the AI Will Do
          </h3>
          <ul className="space-y-2 text-slate-700 text-sm">
            {[
              "Reference relevant guidelines by name in responses",
              "Remind nurses of professional accountability under the NMC Code",
              "Use correct clinical terminology",
              "Flag safety concerns prominently",
              "Recommend escalation to a consultant rheumatologist when appropriate",
              "Stay within the UK regulatory framework at all times",
            ].map((item) => (
              <li key={item} className="flex items-start gap-3 bg-teal-50 rounded-xl p-3 text-teal-900">
                <CheckCircle2 className="h-4 w-4 text-teal-500 shrink-0 mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </section>

        <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <XCircle className="h-5 w-5 text-rose-500" />
            4. What the AI Will Not Do (Mandatory Refusals)
          </h3>
          <p className="text-slate-600 text-sm">These refusal rules are hardcoded into the system prompt and cannot be overridden by user messages:</p>
          <div className="space-y-3">
            {[
              {
                rule: "Provide diagnoses",
                response: "Will acknowledge clinical features and diagnostic criteria but redirect: 'Only a clinician with access to the full clinical picture can make a diagnosis.'"
              },
              {
                rule: "Prescribe or recommend dosage changes",
                response: "Will redirect to the prescribing clinician and BNF/trust prescribing policy."
              },
              {
                rule: "Interpret investigation results as diagnostic conclusions",
                response: "Will explain reference ranges and guideline thresholds but will not state what a result means diagnostically."
              },
              {
                rule: "Advise stopping prescribed medication",
                response: "Will always direct to the prescribing clinician; if urgent safety concern, will advise immediate escalation."
              },
            ].map((item) => (
              <div key={item.rule} className="bg-rose-50 border border-rose-200 rounded-xl p-4">
                <p className="text-rose-900 font-semibold text-sm flex items-center gap-2">
                  <XCircle className="h-4 w-4 text-rose-500 shrink-0" />
                  Will NOT: {item.rule}
                </p>
                <p className="text-rose-800 text-sm mt-1 ml-6">{item.response}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Shield className="h-5 w-5 text-teal-600" />
            5. Audit Logging
          </h3>
          <p className="text-slate-700 leading-relaxed text-sm">
            Every AI inference request is recorded in the <code className="bg-slate-100 px-1.5 py-0.5 rounded">ai_audit_logs</code> table. The following metadata is captured:
          </p>
          <ul className="space-y-1 text-sm text-slate-700 list-disc list-inside">
            <li>User ID (Clerk), role (nurse/patient), and conversation ID</li>
            <li>Model used and token counts (prompt and completion)</li>
            <li>Request timestamp, completion timestamp, and duration in milliseconds</li>
            <li>HTTP status code (200 for success, 500 for error)</li>
          </ul>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
            <strong>Important:</strong> Message content (the text of conversations) is never duplicated into audit logs. Only metadata is recorded.
          </div>
          <p className="text-sm text-slate-600">
            Audit logs are retained for the same period as conversation data (365 days for nurse accounts) and then permanently deleted. You can export your data at any time from your <a href="/nurse/settings" className="text-teal-700 underline">Settings page</a>.
          </p>
        </section>

        <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <Clock className="h-5 w-5 text-teal-600" />
            6. System Prompt Version History
          </h3>
          <div className="space-y-3">
            <div className="flex items-start gap-4 bg-teal-50 border border-teal-200 rounded-xl p-4">
              <Badge className="bg-teal-100 text-teal-800 border-teal-200 shrink-0">v2025-05</Badge>
              <div className="text-sm text-teal-900">
                <p className="font-semibold">May 2025 — Initial governance version</p>
                <ul className="list-disc list-inside mt-1 space-y-0.5 text-teal-800">
                  <li>Added BNF to grounding sources</li>
                  <li>Added four mandatory refusal rules (diagnose, prescribe, interpret investigations, stop medication)</li>
                  <li>Added version header and review date</li>
                  <li>Extracted prompts to auditable <code>prompts.ts</code> file</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white rounded-2xl p-8 border border-slate-200 space-y-4">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <MessageSquare className="h-5 w-5 text-teal-600" />
            7. How to Raise Concerns
          </h3>
          <p className="text-slate-700 text-sm leading-relaxed">
            If the AI provides a response that appears clinically inaccurate, inappropriate, or potentially unsafe:
          </p>
          <ol className="list-decimal list-inside space-y-2 text-sm text-slate-700">
            <li>Do not act on the response without applying your own clinical judgment.</li>
            <li>Escalate any patient safety concern immediately through your trust's normal channels.</li>
            <li>Report the issue to the RheumHub governance team at: <strong>governance@medantixhealth.co.uk</strong></li>
            <li>Include the conversation context and the problematic response in your report (do not include patient-identifiable information).</li>
          </ol>
          <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-700">
            <strong>NMC accountability reminder:</strong> Your duty of candour and professional accountability under the NMC Code applies at all times, regardless of whether you have used AI assistance.
          </div>
        </section>

        <div className="bg-teal-50 border border-teal-200 rounded-2xl p-6 text-sm text-teal-800">
          <p><strong>Policy review:</strong> This governance framework is reviewed bi-annually (next review: November 2025). Changes to the AI system prompts are documented in the version history above. All substantive changes require clinical lead approval.</p>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 text-sm text-amber-900">
          <p className="font-semibold mb-1">Concerns or feedback about AI outputs?</p>
          <p>If you receive an AI response that appears clinically unsafe, inaccurate, or outside scope, please report it to your line manager and raise a concern via your trust's incident reporting system. Do not act on a response you consider clinically unsafe.</p>
        </div>

      </div>
    </NurseLayout>
  );
}
